import { useCallback, useMemo, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { organizationWizardSteps, type OrganizationSetupStepKey } from '@brimo/shared';
import { Skeleton } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { api } from '../../../lib/api';
import { sessionQueryKey } from '../../../lib/session';
import { WizardHeader, WizardLayout } from '../ui';
import { organizationKeys, postStep, useWizard } from './api';
import { LoadError } from './LoadError';
import {
  MobileBrandStep,
  MobileCompanyStep,
  MobileContinueStep,
  MobileSecurityStep,
  MobileTeamStep,
} from './mobile/MobileSteps';
import { BrandStep } from './steps/BrandStep';
import { CompanyStep } from './steps/CompanyStep';
import { DomainStep } from './steps/DomainStep';
import { LegalTextsStep } from './steps/LegalTextsStep';
import { SecurityStep } from './steps/SecurityStep';
import { TeamStep } from './steps/TeamStep';
import { TelephonyStep } from './steps/TelephonyStep';
import type { SetupOrganization, StepAction } from './types';
import { WizardContext, type WizardNav } from './wizardContext';
import { isWizardSegment, keyOf, segmentOf, type WizardSegment } from './wizardSteps';
import styles from './AdminWizard.module.css';

/** Column widths of the frames: 860 (Firmendaten), 1040 (Team), 940 otherwise. */
const widths: Partial<Record<WizardSegment, number>> = { company: 860, team: 1040 };

/** `/setup/organization`: the first open step (or the 2FA once every step was answered). */
export function AdminWizardIndex() {
  const wizard = useWizard();
  if (wizard.isPending) return <WizardSkeleton />;
  if (wizard.isError) {
    return (
      <WizardLayout width={940}>
        <LoadError
          what="Die Einrichtung konnte nicht geladen werden"
          error={wizard.error}
          onRetry={() => void wizard.refetch()}
        />
      </WizardLayout>
    );
  }
  return <Navigate to={`/setup/organization/${segmentOf(wizard.data.current)}`} replace />;
}

function WizardSkeleton() {
  return (
    <WizardLayout width={940}>
      <div className={styles.skeleton} aria-busy="true" aria-label="Wird geladen">
        <Skeleton width={120} height={12} />
        <Skeleton width={320} height={34} />
        <Skeleton variant="block" height={360} />
      </div>
    </WizardLayout>
  );
}

/**
 * The admin wizard (Onboarding Admin 03–10, M2–M4) at `/setup/organization/:step` below `RequireSetup`: stepper
 * header from `GET /setup/organization`, one step per route, then the 2FA and `POST /setup/organization/finish`.
 */
export function AdminWizard() {
  const { step } = useParams();
  const wizard = useWizard();
  if (!isWizardSegment(step)) return <Navigate to="/setup/organization" replace />;
  if (wizard.isPending) return <WizardSkeleton />;
  if (wizard.isError) {
    return (
      <WizardLayout width={940}>
        <LoadError
          what="Die Einrichtung konnte nicht geladen werden"
          error={wizard.error}
          onRetry={() => void wizard.refetch()}
        />
      </WizardLayout>
    );
  }
  return <WizardPage segment={step} wizard={wizard.data} />;
}

function WizardPage({ segment, wizard }: { segment: WizardSegment; wizard: SetupOrganization }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mobile = useIsMobile();

  const go = useCallback((next: WizardSegment) => void navigate(`/setup/organization/${next}`), [navigate]);
  const complete = useCallback(
    async (key: OrganizationSetupStepKey, action: StepAction, next: WizardSegment) => {
      const updated = await postStep(key, action);
      queryClient.setQueryData(organizationKeys.wizard, updated);
      await navigate(`/setup/organization/${next}`);
    },
    [navigate, queryClient],
  );
  const finish = useCallback(async () => {
    const updated = await api<SetupOrganization>('/setup/organization/finish', { method: 'POST', body: {} });
    queryClient.setQueryData(organizationKeys.wizard, updated);
    await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    await navigate('/setup/checklist', { replace: true });
  }, [navigate, queryClient]);
  const nav = useMemo<WizardNav>(() => ({ wizard, go, complete, finish }), [wizard, go, complete, finish]);

  const steps = wizard.steps.filter((entry) =>
    (organizationWizardSteps as readonly OrganizationSetupStepKey[]).includes(entry.key),
  );
  let content: ReactNode;
  if (mobile) {
    content = mobileStep(segment);
  } else {
    content = (
      <WizardLayout
        width={widths[segment] ?? 940}
        gap={segment === 'brand' ? 24 : 22}
        header={<WizardHeader steps={steps} currentKey={keyOf(segment)} userLabel={wizard.userLabel} />}
      >
        {desktopStep(segment)}
      </WizardLayout>
    );
  }
  return <WizardContext.Provider value={nav}>{content}</WizardContext.Provider>;
}

function desktopStep(segment: WizardSegment): ReactNode {
  switch (segment) {
    case 'brand':
      return <BrandStep />;
    case 'company':
      return <CompanyStep />;
    case 'legal-texts':
      return <LegalTextsStep />;
    case 'domain':
      return <DomainStep />;
    case 'telephony':
      return <TelephonyStep />;
    case 'team':
      return <TeamStep />;
    case 'security':
      return <SecurityStep />;
  }
}

/** Phones: Marke (M2), Firmendaten, „Am Rechner fortsetzen" (M3) for the three computer steps, Team (M4), 2FA. */
function mobileStep(segment: WizardSegment): ReactNode {
  switch (segment) {
    case 'brand':
      return <MobileBrandStep />;
    case 'company':
      return <MobileCompanyStep />;
    case 'legal-texts':
    case 'domain':
    case 'telephony':
      return <MobileContinueStep />;
    case 'team':
      return <MobileTeamStep />;
    case 'security':
      return <MobileSecurityStep />;
  }
}
